<script setup>
import { nextTick, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import Dialog from 'next/dialog/Dialog.vue';
import CreateTaskForm from './CreateTaskForm.vue';

const emit = defineEmits(['close']);
const { t } = useI18n();
const store = useStore();
const getters = useStoreGetters();

const dialogRef = ref(null);
const isOpen = ref(false);
const formRef = ref(null);
const uiFlags = getters['wgptTasks/getUIFlags'];

const onClose = () => {
  isOpen.value = false;
  emit('close');
};

const open = () => {
  isOpen.value = true;
  nextTick(() => {
    if (!dialogRef.value) return;
    dialogRef.value.open();
  });
};

const close = () => {
  dialogRef.value.close();
  onClose();
};

const handleSubmit = async () => {
  try {
    if (!formRef.value) return;
    const { validate, state } = formRef.value;

    const isFormValid = await validate();
    if (!isFormValid) return;

    const task = {
      ...state,
      active: true,
    };

    await store.dispatch('wgptTasks/create', task);

    useAlert(t('WGPT_TASKS.ADD.API.SUCCESS_MESSAGE'));
    close();
  } catch (error) {
    const errorMessage =
      error?.response?.message || t('WGPT_TASKS.ADD.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  }
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('WGPT_TASKS.ADD.TITLE')"
    :description="t('WGPT_TASKS.ADD.DESCRIPTION')"
    :confirm-button-label="t('WGPT_TASKS.ADD.FORM.SAVE')"
    :cancel-button-label="t('WGPT_TASKS.ADD.FORM.CANCEL')"
    :is-loading="uiFlags.isCreating"
    :disable-confirm-button="uiFlags.isCreating"
    @confirm="handleSubmit"
    @close="onClose"
  >
    <CreateTaskForm v-if="isOpen" ref="formRef" @submit="handleSubmit" />
  </Dialog>
</template>
